import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { ConfirmDialog } from '@jumpstone/ui-react';
import type {
  LeaveActionFailure,
  LeaveActionResult,
} from '../../data/use_leave_requests';
import type { LeaveRequest } from '../../data/wagepro_store_leave';

/**
 * Approve confirmation dialog. Even though approve carries no destructive
 * copy (the request just moves from `pending` to `approved`), approval
 * decrements the balance-tracked leave types' running balance via a
 * compare-and-swap UPDATE in `approveLeave` (`payroll.ts` § balance CAS) —
 * a real, irreversible side effect on payroll state. Acceptance criteria
 * for #23310 requires approve and reject BOTH to run behind a confirmation.
 *
 * Unlike `LeaveRejectDialog` this dialog collects no free-text input —
 * `payroll.approveLeave` takes only `{firm_id, entity_id, request_id}`.
 */
export function LeaveApproveConfirmDialog({
  request,
  onOpenChange,
  approve,
  onFailure,
}: {
  request: LeaveRequest | null;
  onOpenChange: (open: boolean) => void;
  approve: ((request_id: string) => Promise<LeaveActionResult>) | undefined;
  /** `request_id` is passed explicitly so the parent can attribute the failure to the request that was in flight, not to whichever row is `approve_target` at resolution time. */
  onFailure: (request_id: string, failure: LeaveActionFailure) => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [is_submitting, set_is_submitting] = useState(false);

  const handle_confirm = (): void => {
    if (!request || !approve) return;
    const in_flight_id = request.id;
    set_is_submitting(true);
    void approve(in_flight_id)
      .then((result) => {
        if (result.outcome === 'success') {
          onOpenChange(false);
        } else {
          // Close the dialog on failure too so the row-level error is not
          // hidden behind the modal backdrop — the parent renders the
          // classified failure inline on the row via onFailure.
          onFailure(in_flight_id, result.failure);
        }
      })
      .finally(() => {
        set_is_submitting(false);
      });
  };

  return (
    <ConfirmDialog
      open={request !== null}
      onOpenChange={onOpenChange}
      title={t('leave.approve_dialog_title')}
      description={t('leave.approve_dialog_description')}
      confirmLabel={t('common.approve')}
      cancelLabel={t('common.cancel')}
      loading={is_submitting}
      onConfirm={handle_confirm}
    />
  );
}
